const METRIC_NAME_HINTS = [
  { match: /general summary/i, id: 21 },
  { match: /root cause/i, id: 20 },
  { match: /key drivers|temel etken/i, id: 23 },
  { match: /highlights|öne çıkan/i, id: 25 },
  { match: /decision analysis|karar analizi/i, id: 27 },
  { match: /hot terms|sıcak terim/i, id: 28 },
];

const DETAIL_ROW_ORDER = [
  "dashboard",
  "metric",
  "widget",
  "vendors",
  "pivot",
  "date_range",
  "topics",
];

const SKIP_DETAIL_LABELS = new Set([
  "summary",
  "regenerate",
  "brand",
  "workspace_group",
]);
const SKIP_DETAIL_VALUE_PATTERNS = [
  /^widget_id=\d+$/i,
  /^regenerate:/i,
];

export const AI_CREDIT_SOURCE_LABEL_KEYS = {
  my_workspace: "ai_credits_page.sourceMyWorkspace",
  monthly_ai_report: "ai_credits_page.sourceMonthlyReport",
  dashboard: "ai_credits_page.sourceDashboard",
  other: "ai_credits_page.sourceOther",
};

export const AI_CREDIT_ACTION_LABEL_KEYS = {
  widget_created: "ai_credits_page.actionWidgetCreated",
  report_generated: "ai_credits_page.actionReportGenerated",
  root_cause_analysis: "ai_credits_page.actionRootCause",
  ad_hoc_request: "ai_credits_page.actionAdHoc",
};

export const AI_CREDIT_DETAIL_LABEL_KEYS = {
  summary: "ai_credits_page.detailSummary",
  dashboard: "ai_credits_page.detailDashboard",
  widget: "ai_credits_page.detailMetric",
  metric: "ai_credits_page.detailMetric",
  pivot: "ai_credits_page.detailPivot",
  vendors: "ai_credits_page.detailVendors",
  date_range: "ai_credits_page.detailDateRange",
  topics: "ai_credits_page.detailTopics",
};

export function formatAiCreditRunDate(iso, locale = "tr-TR") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function parseLocalDate(iso) {
  if (!iso) return null;
  const match = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function addMonthsLocal(dateObj, months) {
  const next = new Date(dateObj.getTime());
  next.setMonth(next.getMonth() + months);
  return next;
}

export function resolveSummaryResetDate(summary) {
  if (!summary) return null;
  if (summary.reset_date) return summary.reset_date;
  if (summary.cycle_end) return summary.cycle_end;
  if (summary.cycle_start) {
    const start = parseLocalDate(summary.cycle_start);
    if (start) {
      return addMonthsLocal(start, 1).toISOString().slice(0, 10);
    }
  }
  return null;
}

export function resolveSummaryDaysUntilReset(summary) {
  if (!summary) return null;
  if (Number.isFinite(summary.days_until_reset)) {
    return Math.max(0, Number(summary.days_until_reset));
  }
  const resetDate = resolveSummaryResetDate(summary);
  const reset = parseLocalDate(resetDate);
  if (!reset) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  reset.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((reset - today) / (1000 * 60 * 60 * 24)));
}

export function formatResetDate(iso, locale = "tr-TR") {
  const parsed = parseLocalDate(iso);
  if (!parsed) return null;
  try {
    return parsed.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function formatCreditUsedLabel(count, t) {
  const n = Number(count) || 0;
  if (n <= 0) return "—";
  return t("ai_credits_page.creditUsedAmount", { count: n });
}

export function formatAiCreditLlmLabel(provider, model) {
  const prov = String(provider || "").trim().toLowerCase();
  const mdl = String(model || "").trim();
  if (!prov && !mdl) return "—";
  if (prov === "vertex") {
    return mdl ? `Vertex · ${mdl}` : "Vertex";
  }
  if (prov === "deepseek") {
    return mdl ? `DeepSeek · ${mdl}` : "DeepSeek";
  }
  if (prov && mdl) return `${prov} · ${mdl}`;
  return prov || mdl || "—";
}

export function formatAiCreditVendorLabel(record) {
  const direct = String(record?.vendor_label || "").trim();
  if (direct) return direct;
  const values = Array.isArray(record?.pivot_values) ? record.pivot_values : [];
  const names = values.map((v) => String(v || "").trim()).filter(Boolean);
  return names.length ? names.join(", ") : "—";
}

export function formatAiCreditMetricLabel(record, t) {
  const direct = String(record?.metric_label || "").trim();
  if (direct) return localizeMetricName(direct, t);
  const rawRows = Array.isArray(record?.detail_rows) ? record.detail_rows : [];
  const metricRow = rawRows.find((row) => {
    const label = String(row?.label || "").trim().toLowerCase();
    return label === "metric" || label === "widget";
  });
  if (metricRow?.value) {
    return localizeMetricName(String(metricRow.value), t);
  }
  return "—";
}

export function formatAiCreditDateRangeLabel(record) {
  const direct = String(record?.date_range_label || "").trim();
  if (direct) return direct;
  const rawRows = Array.isArray(record?.detail_rows) ? record.detail_rows : [];
  const rangeRow = rawRows.find((row) => {
    const label = String(row?.label || "").trim().toLowerCase();
    return label === "date_range" || label === "date range";
  });
  if (rangeRow?.value) {
    return String(rangeRow.value).replace(/\s->\s/g, " – ").replace(/\s→\s/g, " – ");
  }
  return "—";
}

function localizeMetricName(value, t) {
  if (!value || typeof value !== "string") return value;
  const cleaned = value.replace(/^regenerate:\s*/i, "").trim();
  for (const hint of METRIC_NAME_HINTS) {
    if (hint.match.test(cleaned)) {
      const key = `welcome.metricName_${hint.id}`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
  }
  return cleaned;
}

function humanizePivotValue(value, t) {
  if (!value) return value;
  const str = String(value);
  const valuesMatch = str.match(/^(.+?)\s*\((\d+)\s*values?\)$/i);
  if (valuesMatch) {
    return null;
  }
  return str;
}

function formatVendorList(values) {
  if (!Array.isArray(values)) return null;
  const names = values.map((v) => String(v || "").trim()).filter(Boolean);
  if (!names.length) return null;
  return names.join(", ");
}

function humanizeDateRangeValue(value) {
  if (!value) return value;
  return String(value).replace(/\s->\s/g, " – ").replace(/\s→\s/g, " – ");
}

function normalizeDetailLabel(rawLabel) {
  const label = String(rawLabel || "").trim().toLowerCase();
  if (label === "widget" || label === "metric") return "metric";
  if (label === "date range" || label === "date_range") return "date_range";
  if (label === "workspace group" || label === "workspace_group") {
    return "workspace_group";
  }
  if (label === "regenerate") return "regenerate";
  return label;
}

function humanizeDashboardValue(value) {
  if (!value) return value;
  const str = String(value).trim();
  const quoted = [...str.matchAll(/'([^']+)'/g)].map((match) => match[1]);
  if (quoted.length) return quoted.join(", ");
  return str.replace(/^\[|\]$/g, "").trim();
}

function shouldSkipDetailRow(row) {
  const label = normalizeDetailLabel(row.label);
  const value = String(row.value || "").trim();
  if (!value) return true;
  if (SKIP_DETAIL_LABELS.has(label)) return true;
  return SKIP_DETAIL_VALUE_PATTERNS.some((pattern) => pattern.test(value));
}

function formatDetailValue(label, value, t) {
  const normalized = normalizeDetailLabel(label);
  if (normalized === "metric" || normalized === "widget") {
    return localizeMetricName(value, t);
  }
  if (normalized === "dashboard") {
    return humanizeDashboardValue(value);
  }
  if (normalized === "pivot") {
    const pivotValue = humanizePivotValue(value, t);
    return pivotValue || null;
  }
  if (normalized === "date_range") {
    return humanizeDateRangeValue(value);
  }
  return String(value).trim();
}

export function buildHumanCreditDetailRows(record, t) {
  const rawRows = Array.isArray(record?.detail_rows) ? record.detail_rows : [];
  const byLabel = new Map();

  rawRows.forEach((row) => {
    if (shouldSkipDetailRow(row)) return;
    const label = normalizeDetailLabel(row.label);
    const value = formatDetailValue(label, row.value, t);
    if (!value) return;
    if (!byLabel.has(label) || label === "metric") {
      byLabel.set(label, value);
    }
  });

  if (record?.dashboard_name && !byLabel.has("dashboard")) {
    byLabel.set("dashboard", record.dashboard_name);
  }

  const vendorList = formatVendorList(record?.pivot_values);
  if (vendorList && !byLabel.has("vendors")) {
    byLabel.set("vendors", vendorList);
  }

  const ordered = [];
  DETAIL_ROW_ORDER.forEach((label) => {
    if (byLabel.has(label)) {
      ordered.push({ label, value: byLabel.get(label) });
      byLabel.delete(label);
    }
  });
  byLabel.forEach((value, label) => {
    ordered.push({ label, value });
  });

  return ordered;
}

export function buildCreditRecordLinks(record, t) {
  const links = [];
  if (record?.welcome_widget_id) {
    links.push(
      t("ai_credits_page.linkWidgetRef", { id: record.welcome_widget_id }),
    );
  }
  if (record?.dashboard_id) {
    links.push(
      t("ai_credits_page.linkDashboardRef", {
        name: record.dashboard_name || `#${record.dashboard_id}`,
      }),
    );
  }
  if (record?.ai_report_id) {
    links.push(
      t("ai_credits_page.linkReportRef", { id: record.ai_report_id }),
    );
  }
  return links;
}
